import { inject, Injectable, InjectionToken } from '@angular/core';

export const PERSISTENT_STORAGE = new InjectionToken<BasePersistentStorage>('PERSISTENT_STORAGE');
export const AUTH_SESSION_STORAGE_TYPE = new InjectionToken<StorageType>('AUTH_SESSION_STORAGE_TYPE');

/** Available storage types for the StorageAdapter. */
export enum StorageType {
  PERSISTENT = 'PERSISTENT',
  SESSION = 'SESSION',
  MEMORY = 'MEMORY',
}

/**
 * Unified interface for typed read and write operations on a storage backend.
 * Values are serialized as JSON internally.
 */
export type Storage = {
  /**
   * Reads a value by key.
   *
   * @param {string} key - The storage key to look up.
   * @returns {Promise<T | null>} The deserialized value, or `null` if no entry exists or the data is corrupted.
   */
  getValue: <T>(key: string) => Promise<T | null>;

  /**
   * Serializes the value as JSON and stores it under the given key.
   *
   * @param {string} key - The storage key to write to.
   * @param {T} value - The value to serialize and persist.
   * @returns {Promise<void>}
   */
  setValue: <T>(key: string, value: T) => Promise<void>;

  /**
   * Removes the entry for the given key.
   *
   * @param {string} key - The storage key to delete.
   * @returns {Promise<void>}
   */
  removeValue: (key: string) => Promise<void>;
};

/**
 * Low-level interface for platform-specific persistence implementations (e.g. Capacitor Preferences).
 * Operates exclusively on raw strings.
 */
export type BasePersistentStorage = {
  getValue(key: string): Promise<string | null>;
  setValue(key: string, value: string): Promise<void>;
  removeValue(key: string): Promise<void>;
};

/**
 * Central adapter that returns the appropriate {@link Storage} implementation for a given {@link StorageType}.
 *
 * - `PERSISTENT`: platform-specific persistent storage (via {@link PERSISTENT_STORAGE} token)
 * - `SESSION`: browser-native `sessionStorage` (tab lifetime)
 * - `MEMORY`: volatile in-memory storage (process lifetime)
 */
@Injectable({ providedIn: 'root' })
export class StorageAdapter {
  private readonly persistentStorage = inject(PERSISTENT_STORAGE);

  private readonly memoryStorage = new Map<string, string>();

  /**
   * Returns the {@link Storage} implementation for the given type.
   *
   * @param {StorageType} type - The desired storage type.
   * @returns {Storage} The corresponding storage implementation.
   * @throws {Error} If an unknown {@link StorageType} is provided.
   */
  public get(type: StorageType): Storage {
    switch (type) {
      case StorageType.PERSISTENT:
        return this.getPersistentStorage();
      case StorageType.SESSION:
        return this.getSessionStorage();
      case StorageType.MEMORY:
        return this.getMemoryStorage();
      default:
        throw new Error(`Unsupported storage type: ${type}`);
    }
  }

  private getPersistentStorage(): Storage {
    return {
      getValue: async <T>(key: string) => {
        const raw = await this.persistentStorage.getValue(key);
        if (!raw) {
          return null;
        }
        try {
          return JSON.parse(raw) as T;
        } catch {
          return null;
        }
      },
      setValue: async <T>(key: string, value: T) => {
        const raw = JSON.stringify(value);
        await this.persistentStorage.setValue(key, raw);
      },
      removeValue: (key: string) => this.persistentStorage.removeValue(key),
    };
  }

  private getSessionStorage(): Storage {
    return {
      getValue: <T>(key: string) => {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
          return Promise.resolve(null);
        }
        try {
          return Promise.resolve(JSON.parse(raw) as T);
        } catch {
          return Promise.resolve(null);
        }
      },
      setValue: <T>(key: string, value: T) => {
        const raw = JSON.stringify(value);
        sessionStorage.setItem(key, raw);
        return Promise.resolve();
      },
      removeValue: (key: string) => {
        sessionStorage.removeItem(key);
        return Promise.resolve();
      },
    };
  }

  private getMemoryStorage(): Storage {
    return {
      getValue: <T>(key: string) => {
        const raw = this.memoryStorage.get(key) || null;
        if (!raw) {
          return Promise.resolve(null);
        }
        try {
          return Promise.resolve(JSON.parse(raw) as T);
        } catch {
          return Promise.resolve(null);
        }
      },
      setValue: <T>(key: string, value: T) => {
        const raw = JSON.stringify(value);
        this.memoryStorage.set(key, raw);
        return Promise.resolve();
      },
      removeValue: (key: string) => {
        this.memoryStorage.delete(key);
        return Promise.resolve();
      },
    };
  }
}
